import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { ContactDefaultCustomField } from 'src/constants/types';
import { GetContactsParams } from '../types';
import { getVisibleCustomFieldIds } from './customFieldVisibility';

export type ContactsTableStoredState = {
  query?: string;
  sort?: string;
  sortMetadataId?: string | null;
  sortOrder?: 'asc' | 'desc';
  columnVisibility?: GridColumnVisibilityModel;
};

const storageKey = (userId: string) => `bhiContactsTable:${userId}`;
const queryStorageKey = (userId: string) => `bhiContactsTableQuery:${userId}`;

const loadPersistentState = (userId: string): Omit<ContactsTableStoredState, 'query'> => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    delete parsed.query;
    return parsed;
  } catch {
    return {};
  }
};

export const loadContactsTableState = (
  userId?: string | null,
): ContactsTableStoredState => {
  if (!userId) return {};

  const state: ContactsTableStoredState = loadPersistentState(userId);

  // The query lives in sessionStorage and is cleared when the contacts page
  // unmounts, so it survives a page refresh but not reopening the page.
  try {
    const query = sessionStorage.getItem(queryStorageKey(userId));
    if (query) {
      state.query = query;
    }
  } catch {
    // storage unavailable — restore is best-effort
  }

  return state;
};

export const saveContactsTableState = (
  userId: string | null | undefined,
  patch: Partial<Omit<ContactsTableStoredState, 'query'>>,
): void => {
  if (!userId) return;

  try {
    const next = { ...loadPersistentState(userId), ...patch };
    localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // localStorage unavailable (private mode, quota) — persistence is best-effort
  }
};

export const saveContactsTableQuery = (
  userId: string | null | undefined,
  query: string,
): void => {
  if (!userId) return;

  try {
    if (query) {
      sessionStorage.setItem(queryStorageKey(userId), query);
    } else {
      sessionStorage.removeItem(queryStorageKey(userId));
    }
  } catch {
    // storage unavailable — persistence is best-effort
  }
};

export const clearContactsTableQuery = (userId: string | null | undefined): void => {
  if (!userId) return;

  try {
    sessionStorage.removeItem(queryStorageKey(userId));
  } catch {
    // storage unavailable — nothing to clear
  }
};

export const buildRestoredContactsFilter = (
  saved: ContactsTableStoredState,
  customFields: ContactDefaultCustomField[],
): Pick<
  GetContactsParams,
  'query' | 'sort' | 'sortMetadataId' | 'sortOrder' | 'queryMetadataIds'
> => {
  const sortMetadataId =
    saved.sortMetadataId && customFields.some(field => field.metadataId === saved.sortMetadataId)
      ? saved.sortMetadataId
      : null;

  return {
    query: saved.query || undefined,
    sort: sortMetadataId ? undefined : saved.sort,
    sortMetadataId,
    sortOrder: sortMetadataId ? saved.sortOrder || 'asc' : undefined,
    queryMetadataIds: getVisibleCustomFieldIds(customFields, saved.columnVisibility || {}),
  };
};
